import React from 'react';
import { FormattedMessage, injectIntl, IntlShape } from 'react-intl';
import * as mobxReact from 'mobx-react';
import * as IoC from 'typescript-ioc';
import classNames from 'classnames';
import { ISortOptions, ITheadSort, ListLayout } from '../../../../global/tables/table-list-layout';
import OECheckbox from '../../../../global/oe-checkbox/oe-checkbox';
import { SortOrder } from '../../../../../api/shared.dto';
import Text from '../../../../global/text/text';
import TableLoadingRow from '../../../../global/tables/table-loading-row';
import { DateFnsFormats } from '../../../../../infrastructure/date-formatter';
import { DateTimeFormatter } from '../../../../../infrastructure/implementations/date-formatter';
import { IMeetingsReportItem, ReportType } from '../../../../../api/reports.dao';
import './meetings-report-table-layout.scss';
import { MEETINGS, WEBINARS } from '../../../../../constants/api/zoom-api.constants';

interface IProps {
    intl: IntlShape;
    isEmpty: boolean;
    handleCheckAll: (value: React.ChangeEvent<HTMLInputElement>) => void;
    className?: string;
    title?: string;
    list: IMeetingsReportItem[];
    loading: boolean;
    eventTimezone: string;
    checkOne: (value: string) => void;
    isValueChecked: (value: string) => boolean;
    isValueDisabled?: (value: string) => boolean;
    checkedAll: boolean;
    isCheckAllDisabled?: boolean;
    reportType?: ReportType;
}

const dateTimeFormatter = IoC.Container.get<DateTimeFormatter>(DateTimeFormatter);

const MeetingsReportTableLayout = injectIntl(mobxReact.observer((props: IProps) => {
    const {
        intl,
        handleCheckAll,
        list,
        loading,
        eventTimezone,
        isEmpty,
        isValueChecked,
        checkOne,
        className,
        title,
        checkedAll,
        isValueDisabled,
        isCheckAllDisabled = false,
        reportType
    } = props;


    const theads: ITheadSort<string>[] = [
        {
            title: (<OECheckbox
                checked={checkedAll}
                onChange={handleCheckAll}
                classNames="oe-form-field__checkbox"
                isDisabled={isCheckAllDisabled}
            />),
            width: '60px'
        },
        {
            title: intl.formatMessage({
                id: 'oe.reports.dateAndTime',
                description: 'Label text',
                defaultMessage: 'Date & Time'
            })
        },
        {
            title: intl.formatMessage({
                id: 'oe.reports.meetingTopic',
                defaultMessage: 'Topic',
                description: 'Label text',
            })
        },
        {
            title: intl.formatMessage({
                id: 'oe.reports.meetingTrack',
                defaultMessage: 'Track',
                description: 'Label text',
            })
        },
        {
            title: intl.formatMessage({
                id: 'oe.reports.meetingType',
                defaultMessage: 'Meeting Type',
                description: 'Label text',
            })
        },
        {
            title: intl.formatMessage({
                id: 'oe.reports.meetingPresenters',
                defaultMessage: 'Presenters',
                description: 'Label text',
            })
        },
    ];

    const sortOptions: ISortOptions<string> = {
        sortOrder: SortOrder.Asc,
        sortField: 'startDateTime',
        /* eslint-disable-next-line */
        sortRows: () => {},
        theads: theads
    };

    const reportTitle = reportType === ReportType.ZOOM_MEETING_REGISTRATION_REPORT ? MEETINGS : WEBINARS;

    return (
        <div className={classNames('meetings-report-popover-body', className)}>
                    {title && <div className='meetings-report-popover-body__header'>
                        <Text message={title}/>
                    </div> }
                    <ListLayout
                        sortOptions={sortOptions}
                        isDisabled={isEmpty || loading}
                    >
                        {loading && <TableLoadingRow colSpan={theads.length} />}
                        {!loading && list.map(value => {
                            const date = dateTimeFormatter.dateToFormattedString(
                                value.startDateTime,
                                DateFnsFormats.StringifiedMonthDayYear,
                                eventTimezone
                            ) + ', ' + dateTimeFormatter.dateToFormattedString(
                                value.startDateTime,
                                dateTimeFormatter.getLocalTimeFormat(),
                                eventTimezone
                            );
                            const presenters = value.presenters.join(', ');
                            return <tr
                                    key={value.id}
                                    onDoubleClick={() => checkOne(String(value.id))}
                                    className={classNames({'disabled': isValueDisabled?.(String(value.id))})}
                                >
                                    <td>
                                        <OECheckbox
                                            checked={isValueChecked(String(value.id))}
                                            onChange={() => checkOne(String(value.id))}
                                            classNames='oe-form-field__checkbox'
                                            isDisabled={isValueDisabled?.(String(value.id))}
                                        />
                                    </td>
                                    <Text tag='td' message={date} />
                                    <Text tag='td' message={value.topic} withEllipsis />
                                    <Text tag='td' message={value.track} withEllipsis />
                                    <Text tag='td' message={value.type} withEllipsis />
                                    <Text tag='td' message={presenters} withEllipsis />
                                </tr>;
                        })}
                    </ListLayout>
                    {isEmpty &&
                        <div className='empty-list-message'>
                            <FormattedMessage
                                description='Meeting list empty text'
                                defaultMessage={`There are no ${reportTitle} scheduled in this event.`}
                                id='oe.reports.emptyMeetingListText'
                            />
                        </div>
                    }
                </div>
    );
}));


export { MeetingsReportTableLayout };